// src/components/subcontractors/tabs/SubActiveJobsTab.jsx
import { useState } from 'react';
import { useSubcontractors } from '../../../context/SubcontractorContext';
import JobFormModal from '../modals/JobFormModal';
import PaymentModal from '../modals/PaymentModal';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseLocalDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(str) {
  return parseLocalDate(str).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount) {
  return `$${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

// ─── Style maps ───────────────────────────────────────────────────────────────

const JOB_STATUS_STYLES = {
  scheduled:   { bg: '#2E8BF022', border: '#2E8BF0', text: '#2E8BF0', label: 'Scheduled' },
  in_progress: { bg: '#F59E0B22', border: '#F59E0B', text: '#F59E0B', label: 'In Progress' },
};

const PAYMENT_STATUS_STYLES = {
  paid:    { bg: '#10B98122', border: '#10B981', text: '#10B981', label: 'Paid' },
  overdue: { bg: '#EF444422', border: '#EF4444', text: '#EF4444', label: 'Overdue' },
  partial: { bg: '#F59E0B22', border: '#F59E0B', text: '#F59E0B', label: 'Partial' },
  pending: { bg: '#64748B22', border: '#64748B', text: '#64748B', label: 'Pending' },
};

// ─── Sorting ──────────────────────────────────────────────────────────────────

const STATUS_ORDER = { in_progress: 0, scheduled: 1 };

function sortActiveJobs(jobs) {
  return [...jobs].sort((a, b) => {
    const statusDiff = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
    if (statusDiff !== 0) return statusDiff;

    if (!a.scheduledDate && !b.scheduledDate) return 0;
    if (!a.scheduledDate) return 1;
    if (!b.scheduledDate) return -1;
    return parseLocalDate(a.scheduledDate) - parseLocalDate(b.scheduledDate);
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusPill({ styles, label }) {
  return (
    <span
      className="px-2 py-0.5 text-[var(--wm-text-ui)] rounded-full border"
      style={{ backgroundColor: styles.bg, borderColor: styles.border, color: styles.text }}
    >
      {label}
    </span>
  );
}

function JobCard({ job, onRecordPayment, onEdit }) {
  const { getJobPaymentSummary, updateJob } = useSubcontractors();
  const { amountPaid, amountOwed, status: payStatus } = getJobPaymentSummary(job);

  const agreedPrice = Number(job.agreedPrice || 0);
  const progressPct = agreedPrice > 0
    ? Math.min(100, (amountPaid / agreedPrice) * 100)
    : 0;

  const jobStyles = JOB_STATUS_STYLES[job.status] || JOB_STATUS_STYLES.scheduled;
  const payStyles = PAYMENT_STATUS_STYLES[payStatus] || PAYMENT_STATUS_STYLES.pending;

  const handleMarkComplete = () => {
    updateJob(job.id, { status: 'completed', completedDate: new Date().toISOString() });
  };

  const handleArchive = () => {
    updateJob(job.id, { status: 'archived' });
  };

  return (
    <div className="bg-white dark:bg-[#0F1923] rounded-xl p-4 border border-gray-200 dark:border-[#1E3048]">
      {/* Top row */}
      <div className="flex justify-between items-start gap-3">
        <div className="min-w-0">
          <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate">{job.projectRef}</p>
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] truncate">{job.customerName}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <StatusPill styles={jobStyles} label={jobStyles.label} />
          <StatusPill styles={payStyles} label={payStyles.label} />
        </div>
      </div>

      {/* Vehicle + scope */}
      <div className="mt-2">
        {job.vehicleLabel && (
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{job.vehicleLabel}</p>
        )}
        {job.scope && (
          <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-1 line-clamp-2">{job.scope}</p>
        )}
      </div>

      {/* Payment progress */}
      <div className="flex gap-4 items-center mt-3">
        <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Agreed: {formatCurrency(agreedPrice)}</span>
        <span className="text-[var(--wm-text-ui)] text-[#10B981]">Paid: {formatCurrency(amountPaid)}</span>
        <span
          className="text-[var(--wm-text-ui)]"
          style={{ color: amountOwed > 0 ? '#F59E0B' : '#10B981' }}
        >
          Owed: {formatCurrency(amountOwed)}
        </span>
        <div className="h-1 flex-1 bg-gray-200 dark:bg-[#243348] rounded-full">
          <div
            className="h-1 bg-[#10B981] rounded-full"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Scheduled date */}
      {job.scheduledDate && (
        <p className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] mt-2">
          Scheduled: {formatDate(job.scheduledDate)}
        </p>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-[#1E3048] flex-wrap">
        {amountOwed > 0 && (
          <button
            onClick={() => onRecordPayment(job)}
            className="px-3 py-1.5 text-[var(--wm-text-ui)] font-medium bg-[#10B981] hover:bg-[#059669] text-white rounded-lg transition-colors"
          >
            Record Payment
          </button>
        )}
        {job.status === 'in_progress' && (
          <button
            onClick={handleMarkComplete}
            className="px-3 py-1.5 text-[var(--wm-text-ui)] border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] hover:border-[#2E8BF0] rounded-lg transition-colors"
          >
            Mark Complete
          </button>
        )}
        <div className="flex gap-1 ml-auto">
          <button
            onClick={() => onEdit(job)}
            className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors px-2 py-1.5"
          >
            Edit
          </button>
          <button
            onClick={handleArchive}
            className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#EF4444] transition-colors px-2 py-1.5"
          >
            Archive
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function SubActiveJobsTab({ sub }) {
  const { getSubJobs } = useSubcontractors();

  // jobModal: null | 'new' | job-object
  const [jobModal, setJobModal] = useState(null);
  // payModal: null | job-object
  const [payModal, setPayModal] = useState(null);

  const allJobs = getSubJobs(sub.id);
  const activeJobs = sortActiveJobs(
    allJobs.filter(j => j.status === 'scheduled' || j.status === 'in_progress'),
  );

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[var(--wm-text-ui)] font-semibold text-[#64748B] dark:text-[#7D93AE] uppercase tracking-wide">
            Active Jobs
          </span>
          <span
            className="px-1.5 py-0.5 text-[var(--wm-text-ui)] font-medium rounded-full"
            style={{ backgroundColor: '#2E8BF022', color: '#2E8BF0', border: '1px solid #2E8BF055' }}
          >
            {activeJobs.length}
          </span>
        </div>
        <button
          onClick={() => setJobModal('new')}
          className="px-3 py-1.5 text-[var(--wm-text-ui)] font-medium bg-[#2E8BF0] hover:bg-[#1a7de0] text-white rounded-lg transition-colors"
        >
          Assign Job
        </button>
      </div>

      {/* Empty state */}
      {activeJobs.length === 0 ? (
        <div className="bg-white dark:bg-[#0F1923] rounded-xl p-8 border border-gray-200 dark:border-[#1E3048] text-center text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">
          No active jobs. Click &lsquo;Assign Job&rsquo; to get started.
        </div>
      ) : (
        activeJobs.map(job => (
          <JobCard
            key={job.id}
            job={job}
            onRecordPayment={j => setPayModal(j)}
            onEdit={j => setJobModal(j)}
          />
        ))
      )}

      {/* Modals */}
      {jobModal !== null && (
        <JobFormModal
          subcontractorId={sub.id}
          job={jobModal === 'new' ? null : jobModal}
          onClose={() => setJobModal(null)}
        />
      )}
      {payModal !== null && (
        <PaymentModal
          job={payModal}
          onClose={() => setPayModal(null)}
        />
      )}
    </div>
  );
}
